import type { SimplePrintLine } from '@/print-engine/adapters/simple-document';
import { formatMoney } from '@/utils/format';

function toNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function asRows(v: unknown): any[] {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object' && Array.isArray((v as any).items)) return (v as any).items;
  return [];
}

export function formatEpcEstimatePrintAmount(amount: unknown, currency: string): string {
  return formatMoney(toNum(amount), {
    currency: currency || 'AED',
    locale: 'en-US',
    display: 'code_prefix',
    separator: ' ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtQty(v: unknown): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function labelEnum(v: unknown): string {
  return String(v || '')
    .replace(/_/g, ' ')
    .trim();
}

export type EpcEstimatePrintBundle = {
  estimate: Record<string, unknown>;
  revision: Record<string, unknown>;
  currency: string;
  tenderTitle?: string;
  boq: any[];
  costs: any[];
  indirects: any[];
  contingency: Record<string, unknown> | null;
  duration: Record<string, unknown> | null;
  total: Record<string, unknown> | null;
  pricing: Record<string, unknown> | null;
};

function sectionLine(title: string, id: string): SimplePrintLine {
  return {
    id,
    lineNumber: '',
    description: title,
    cells: {
      type: '',
      code: '',
      qty: '',
      uom: '',
      rate: '',
      amount: '',
    },
    extraLines: [],
    kind: 'section',
  };
}

/** Build landscape print rows: Client BOQ → Direct costs → Indirects → Contingency. */
export function buildEpcEstimatePrintLines(bundle: EpcEstimatePrintBundle): SimplePrintLine[] {
  const currency = bundle.currency || 'AED';
  const lines: SimplePrintLine[] = [];
  let n = 0;

  const boq = asRows(bundle.boq);
  const costs = asRows(bundle.costs);
  const indirects = asRows(bundle.indirects);
  const boqById = new Map(boq.map((b) => [String(b.id), b]));

  lines.push(sectionLine('— CLIENT BOQ —', 'sec-boq'));
  for (const b of boq) {
    n += 1;
    const qty = toNum(b.quantity);
    const rate = toNum(b.unit_rate ?? b.unitRate);
    const amount = toNum(b.amount ?? b.line_amount ?? qty * rate);
    const code = String(b.item_number ?? b.itemNumber ?? b.code ?? '');
    lines.push({
      id: `boq-${b.id}`,
      lineNumber: n,
      description: String(b.description || code || 'BOQ line'),
      cells: {
        type: 'BOQ',
        code,
        qty: fmtQty(qty),
        uom: String(b.uom || ''),
        rate: formatEpcEstimatePrintAmount(rate, currency),
        amount: formatEpcEstimatePrintAmount(amount, currency),
      },
    });
  }
  if (!boq.length) {
    lines.push({
      id: 'boq-empty',
      lineNumber: '',
      description: 'No client BOQ lines',
      cells: { type: '', code: '', qty: '', uom: '', rate: '', amount: '' },
    });
  }

  lines.push(sectionLine('— DIRECT COST LINES —', 'sec-cost'));
  for (const c of costs) {
    n += 1;
    const qty = toNum(c.quantity);
    const rate = toNum(c.rate ?? c.unit_rate);
    const amount = toNum(c.amount ?? c.line_amount ?? qty * rate);
    const boqRef = boqById.get(String(c.client_boq_line_id || ''));
    const code = String(
      boqRef?.item_number ?? boqRef?.itemNumber ?? c.cbs_code ?? c.resource_code ?? ''
    );
    const kind = String(c.resource_kind || c.resourceKind || 'COST');
    lines.push({
      id: `cost-${c.id}`,
      lineNumber: n,
      description: String(c.description || kind),
      cells: {
        type: kind,
        code,
        qty: fmtQty(qty),
        uom: String(c.uom || ''),
        rate: formatEpcEstimatePrintAmount(rate, currency),
        amount: formatEpcEstimatePrintAmount(amount, currency),
      },
      extraLines: code ? [`BOQ ref: ${code}`] : undefined,
    });
  }
  if (!costs.length) {
    lines.push({
      id: 'cost-empty',
      lineNumber: '',
      description: 'No direct cost lines',
      cells: { type: '', code: '', qty: '', uom: '', rate: '', amount: '' },
    });
  }

  lines.push(sectionLine('— INDIRECT / PRELIM COSTS —', 'sec-ind'));
  for (const r of indirects) {
    n += 1;
    const amount = toNum(r.calculated_amount ?? r.fixed_amount);
    lines.push({
      id: `ind-${r.id}`,
      lineNumber: n,
      description: String(r.description || labelEnum(r.category)),
      cells: {
        type: labelEnum(r.category),
        code: labelEnum(r.calculation_method),
        qty: r.quantity != null ? fmtQty(r.quantity) : r.percentage != null ? `${fmtQty(r.percentage)}%` : '',
        uom: labelEnum(r.base_selector) || '',
        rate: r.rate != null ? formatEpcEstimatePrintAmount(r.rate, currency) : '',
        amount: formatEpcEstimatePrintAmount(amount, currency),
      },
    });
  }
  if (!indirects.length) {
    lines.push({
      id: 'ind-empty',
      lineNumber: '',
      description: 'No indirect cost lines',
      cells: { type: '', code: '', qty: '', uom: '', rate: '', amount: '' },
    });
  }

  const cont = bundle.contingency;
  lines.push(sectionLine('— CONTINGENCY —', 'sec-cont'));
  if (cont && (cont.method || cont.calculated_amount != null)) {
    n += 1;
    lines.push({
      id: 'contingency',
      lineNumber: n,
      description: String(cont.remarks || 'Contingency allowance'),
      cells: {
        type: 'CONTINGENCY',
        code: labelEnum(cont.method),
        qty: cont.rate_pct != null ? `${fmtQty(cont.rate_pct)}%` : '',
        uom: '',
        rate: '',
        amount: formatEpcEstimatePrintAmount(cont.calculated_amount, currency),
      },
    });
  } else {
    lines.push({
      id: 'cont-empty',
      lineNumber: '',
      description: 'No contingency configured',
      cells: { type: '', code: '', qty: '', uom: '', rate: '', amount: '' },
    });
  }

  return lines;
}

export function buildEpcEstimatePrintMeta(
  bundle: EpcEstimatePrintBundle
): Array<{ label: string; value: string }> {
  const est = bundle.estimate || {};
  const rev = bundle.revision || {};
  const dur = bundle.duration || {};
  const pricing = bundle.pricing || {};
  const total = bundle.total || {};

  const meta: Array<{ label: string; value: string }> = [
    { label: 'Estimate', value: String(est.estimate_number || '') },
    { label: 'Title', value: String(est.title || '') },
    { label: 'Revision', value: String(rev.revision_label || rev.revision_number || '') },
    { label: 'Status', value: String(rev.status || '') },
    { label: 'Currency', value: bundle.currency || 'AED' },
  ];
  if (bundle.tenderTitle) meta.push({ label: 'Tender', value: bundle.tenderTitle });
  if (dur.overall_duration_months != null || dur.estimate_assumed_months != null) {
    meta.push({
      label: 'Duration',
      value: `${dur.overall_duration_months ?? dur.estimate_assumed_months} mo` +
        (dur.construction_months != null ? ` (construction ${dur.construction_months})` : ''),
    });
  }
  if (pricing.pricing_method) {
    meta.push({ label: 'Pricing', value: labelEnum(pricing.pricing_method) });
  }
  if (pricing.target_margin_percent != null) {
    meta.push({ label: 'Target margin', value: `${fmtQty(pricing.target_margin_percent)}%` });
  }
  if (total.direct_cost != null) {
    meta.push({
      label: 'Direct cost',
      value: formatEpcEstimatePrintAmount(total.direct_cost, bundle.currency),
    });
  }
  if (total.indirect_cost != null) {
    meta.push({
      label: 'Indirect cost',
      value: formatEpcEstimatePrintAmount(total.indirect_cost, bundle.currency),
    });
  }
  return meta.filter((m) => m.value);
}

export function buildEpcEstimatePrintTotalsHeight(bundle: EpcEstimatePrintBundle): number {
  // Rough row count for commercial totals block
  let rows = 6;
  if (bundle.pricing) rows += 3;
  if (bundle.contingency) rows += 1;
  return Math.max(88, rows * 18 + 16);
}
